import { Injectable, inject, type Type } from '@angular/core';
import { MatDialog, type MatDialogConfig, type MatDialogRef } from '@angular/material/dialog';
import { firstValueFrom } from 'rxjs';
import type { DialogPresentation } from './dialog-presentation';
import { MediDialogComponent, type DialogData, type DialogType } from './medi-dialog.component';

@Injectable({ providedIn: 'root' })
export class DialogPresenter {
  private readonly dialog = inject(MatDialog);

  public async showDialog(
    ...args: [DialogPresentation] | [string, string, string, string, DialogType, string?, string?]
  ): Promise<boolean | undefined> {
    const options: DialogPresentation = typeof args[0] === 'string'
      ? { title: args[0], content: args[1], primary: args[2], secondary: args[3], type: args[4], param: args[5], cancel: args[6] }
      : args[0];
    return await this.showComponent<MediDialogComponent, DialogData, boolean>(MediDialogComponent, {
      width: options.width ?? '620px',
      data: {
        title: options.title,
        content: options.content,
        yes: options.primary,
        no: options.secondary,
        cancel: options.cancel ?? '',
        type: options.type,
        param: options.param ?? '',
      },
    });
  }

  public async showComponent<TComponent, TData, TResult>(
    component: Type<TComponent>,
    config: MatDialogConfig<TData>,
  ): Promise<TResult | undefined> {
    const dialogRef = this.openComponent<TComponent, TData, TResult>(component, config);
    return await firstValueFrom(dialogRef.afterClosed());
  }

  public openComponent<TComponent, TData, TResult>(
    component: Type<TComponent>,
    config: MatDialogConfig<TData>,
  ): MatDialogRef<TComponent, TResult> {
    return this.dialog.open<TComponent, TData, TResult>(component, config);
  }
}
